// filepath: c:\Users\plups\WorkSpaces\fullstack\SiSketron\frontend\src\pages\MacroFunzioniPage.jsx
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import TableManager from '../components/data/TableManager';
import MacroFunzioniDetailView from '../components/Macrofunzioni/MacroFunzioniDetailView';
import AdvancedFilterControls from '../common/filters/AdvancedFilterControls';
import CustomActionButtons from '../components/Macrofunzioni/CustomActionButtons';
import BirthdayOverlay from '../components/Birthday/BirthdayOverlay';
import { generatorApi } from '../services/generatorService';
import { logger as servicesLogger } from '../utils/logger';
import useLookupOptions  from '../hooks/useLookupOptions';

// Per mantenere la compatibilità con il codice esistente che usa 'logger.pageLogger',
// e dato che il tuo logger attuale non ha sub-logger come 'pageLogger',
// la cosa più semplice è usare direttamente 'servicesLogger' (che ora è il tuo logger principale).
const logger = servicesLogger;

// Questa macrofunzione deve restare sempre disponibile come prima riga,
// indipendentemente dai filtri, dall'ordinamento e dalla pagina correnti.
const PINNED_MACRO_FILTERS = {
    MAC_IdRif_MacroFunzioni: 2032,
};

// --- Hook per gestire lo stato specifico della pagina MacroFunzioni ---
const useMacroFunzioniState = () => {
    const [selectedModuleIds, setSelectedModuleIds] = useState([]);
    const [cachedTabelleOptions, setCachedTabelleOptions] = useState([]);

    const handleAdvancedFilterChange = useCallback((filterName, value) => {
        if (filterName === 'MAC_Id_Moduli') {
            setSelectedModuleIds(value || []);
        }
    }, []);

    return {
        selectedModuleIds,
        cachedTabelleOptions,
        setCachedTabelleOptions,
        handleAdvancedFilterChange,
    };
};

// Funzione per filtrare le opzioni del lookup delle tabelle
// Esclude le tabelle con prefissi specifici
const filterTabelleForMacroFunzioniPanel = (options) => {
    // Logga le prime 5 opzioni ricevute per ispezione
    logger.info('[MacroFunzioniPage] filterTabelleForMacroFunzioniPanel chiamata. Prime 5 opzioni ricevute:', options && Array.isArray(options) ? JSON.stringify(options.slice(0, 5)) : 'Opzioni non valide o vuote');

    if (!Array.isArray(options) || options.length === 0) {
        logger.warn('[MacroFunzioniPage] filterTabelleForMacroFunzioniPanel: options non è un array valido o è vuoto.', options);
        return [];
    }
    
    const excludedPrefixes = ["DOM", "DOR", "DOT"];
    const filteredOptions = options.filter(option => {
        if (!option) {
            logger.warn('[MacroFunzioniPage] Opzione null/undefined saltata:', option);
            return false;
        }

        // Il campo TAB_Prefisso si trova in option.original.TAB_Prefisso
        const tabPrefisso = option.original?.TAB_Prefisso;
        
        if (!tabPrefisso || typeof tabPrefisso !== 'string') {
            // Se non c'è TAB_Prefisso o non è una stringa, mantieni l'opzione
            logger.debug('[MacroFunzioniPage] Opzione mantenuta (manca TAB_Prefisso o non è stringa):', {
                value: option.value,
                label: option.label,
                prefisso: tabPrefisso
            });
            return true;
        }
        
        // Confronto case-insensitive
        const isExcluded = excludedPrefixes.includes(tabPrefisso.toUpperCase());
        
        if (isExcluded) {
            logger.debug(`[MacroFunzioniPage] Esclusione opzione: ${option.label} (Prefisso: ${tabPrefisso})`);
        } else {
            logger.debug(`[MacroFunzioniPage] Mantenimento opzione: ${option.label} (Prefisso: ${tabPrefisso})`);
        }
        
        return !isExcluded;
    });
    
    logger.info(`[MacroFunzioniPage] filterTabelleForMacroFunzioniPanel: Opzioni originali: ${options.length}, Opzioni filtrate: ${filteredOptions.length}`);
    return filteredOptions;
};

// Configurazione Colonne per SKE_MacroFunzioni
const columnConfig = [
    "MAC_Id_MacroFunzioni=0,ID", // PK, hidden in table, available in forms/detail
    "MAC_IdRif_MacroFunzioni=0,ID Rif.",
    "MAC_Id_Moduli=2,Modulo,lookupName=MAC_Id_Moduli_lookup,maxWidth=43", // Visible in table, lookup handled by lookupConfig
    
    "MAC_Nome=2,Nome Macrofunzione,maxWidth=130",
    "MAC_Descrizione=2,Descrizione,maxWidth=380",
    "MAC_Path=0,Path", // NASCOSTO: cambiato da 2 a 0
    "MAC_Stato=1,Stato,lookupName=statiMacroLookup", // Visible in table, custom cell renderer, lookup for form
    
    "MAC_Organizzazione=0,Organizzazione", // NASCOSTO: cambiato da 1 a 0
    "MAC_Id_Tabelle=1,Descrizione tabella,lookupName=MAC_Tabelle_lookup",
    "MAC_Modifiche=2,Modifiche,maxWidth=380",
    "MAC_Priorita=2,Priorità,maxWidth=45",
    // Fields for Detail View / Forms (visibility 0 or 1)
    "MAC_Area_Params=1,Area Params",
    "MAC_Note=0,Note Dettagliate", // NASCOSTO: cambiato da 1 a 0
    
    "MAC_CampiTabella=0,Struttura Campi Tabella",
    "MAC_Tabelle=0,ID Tabella Associata", // Hidden, but available for logic

    // Operation Flags (visibility 1 for form, handled by CrudOperationsGrid in detail)
    "MAC_Add=1,Flag Add,isBoolean=true", "MAC_DescrizioneAdd=1,Descrizione Add",
    "MAC_Get=1,Flag Get,isBoolean=true", "MAC_DescrizioneGet=1,Descrizione Get",
    "MAC_Src=1,Flag Src,isBoolean=true", "MAC_DescrizioneSrc=1,Descrizione Src",
    "MAC_Srl=1,Flag Srl,isBoolean=true", "MAC_DescrizioneSrl=1,Descrizione Srl",
    "MAC_Upd=1,Flag Upd,isBoolean=true", "MAC_DescrizioneUpd=1,Descrizione Upd",
    "MAC_Set=1,Flag Set,isBoolean=true", "MAC_DescrizioneSet=1,Descrizione Set",
    "MAC_Del=1,Flag Del,isBoolean=true", "MAC_DescrizioneDel=1,Descrizione Del",
    "MAC_Rps=1,Flag Rps,isBoolean=true", "MAC_DescrizioneRps=1,Descrizione Rps",
    "MAC_Prn=1,Flag Prn,isBoolean=true", "MAC_DescrizionePrn=1,Descrizione Prn",
    "MAC_Syn=1,Flag Syn,isBoolean=true", "MAC_DescrizioneSyn=1,Descrizione Syn",

    // Option Flags - NASCOSTI: cambiati da 1 a 0
    "MAC_CreaItem=1,Crea Item Da Tabella,isBoolean=true", // Mantieni visibile
    "MAC_CreaItemFiltro=1,Crea Filtro Ricerche,isBoolean=true", // Mantieni visibile
    "MAC_ControlloValidita=0,Controllo Validità,isBoolean=true", // NASCOSTO: cambiato da 1 a 0
    "MAC_DatiInserimento=0,Traccia Dati Inserimento,isBoolean=true", // NASCOSTO: cambiato da 1 a 0
    "MAC_DatiModifica=0,Traccia Dati Modifica,isBoolean=true", // NASCOSTO: cambiato da 1 a 0
    "MAC_MultiAzienda=1,Multi Azienda,isBoolean=true",

    "MAC_StatiRecord=0,Stato Record DB", // Hidden, managed by backend
    "MAC_InizioValidita=0,Inizio Validità,type=datetime",
    "MAC_FineValidita=0,Fine Validità,type=datetime",
    "MAC_UtenteInserimento=0,Utente Inserimento", "MAC_TerminaleInserimento=0,Terminale Inserimento", "MAC_DataOraInserimento=0,Data/Ora Inserimento",
    "MAC_UtenteModifica=0,Utente Modifica", "MAC_TerminaleModifica=0,Terminale Modifica", "MAC_DataOraModifica=0,Data/Ora Modifica",
    "MAC_Timestamp=0,Timestamp",
];

const filterConfig = [
    // Filtro per Prodotto/Modulo/Stato gestito da AdvancedFilterControls
    {
        field: 'advancedFilters', // Nome fittizio, non corrisponde a un campo DB diretto
        label: 'Filtri Avanzati', // Label opzionale, potrebbe non essere mostrata da GenericFilterPanel per questo tipo
        type: 'customAdvanced', // Nuovo tipo per identificare questo componente speciale
        component: AdvancedFilterControls,
        // Non servono lookupName o lookupType qui perché il componente gestisce i suoi dati
    },
    // Altri filtri regolari
    {
        field: 'MAC_Id_Tabelle',
        label: 'Tabella Principale',
        type: 'lookup',
        lookupName: 'MAC_Tabelle_lookup',
        lookupType: 'select',
        // Nuova proprietà per processare/filtrare le opzioni di lookup
        // prima che vengano passate al SelectField nel GenericFilterPanel
        processLookupOptions: filterTabelleForMacroFunzioniPanel 
    },
    {
        field: 'MAC_Nome',
        label: 'Nome Macrofunzione',
        type: 'text'
    },
    {
        field: 'MAC_Descrizione',
        label: 'Descrizione',
        type: 'text'
    },
];
const lookupConfig = {
    MAC_Id_Moduli_lookup: {
        tableName: 'SKE_Moduli',
        valueField: 'MOD_Id_Moduli',
        labelField: 'MOD_CodiceModulo', 
        additionalFields: ['MOD_DescrizioneModulo'],
    },
    MAC_Tabelle_lookup: {
        tableName: 'SKE_Tabelle',
        valueField: 'TAB_Id_Tabelle', 
        labelField: 'TAB_Descrizione', 
        additionalFields: ['TAB_Id_Moduli','TAB_Prefisso'],
    },
    statiMacroLookup: { 
        tableName: 'DOM_StatiAttivita',
        valueField: 'STM_Codice', 
        labelField: 'STM_Descrizione', 
    },
};

const inputTypes = {
    MAC_Descrizione: 'textarea', MAC_Modifiche: 'textarea',
    MAC_CampiTabella: 'textarea', MAC_Area_Params: 'text', MAC_Note: 'textarea',
    MAC_DescrizioneAdd: 'textarea', MAC_DescrizioneGet: 'textarea', MAC_DescrizioneSrc: 'textarea',
    MAC_DescrizioneSrl: 'textarea', MAC_DescrizioneUpd: 'textarea', MAC_DescrizioneSet: 'textarea',
    MAC_DescrizioneDel: 'textarea', MAC_DescrizioneRps: 'textarea', MAC_DescrizionePrn: 'textarea',
    MAC_DescrizioneSyn: 'textarea',
    MAC_Organizzazione: 'number',
    MAC_Priorita: 'number', MAC_IdRif_MacroFunzioni: 'number', 
    MAC_Tabelle: 'select', // MAC_Tabelle is a select (references MAC_Tabelle_lookup via columnConfig)
    MAC_Id_Moduli: 'select', // Ensure MAC_Id_Moduli is treated as a select in forms
    MAC_InizioValidita: 'datetime-local', MAC_FineValidita: 'datetime-local',
    MAC_Stato: 'select', // MAC_Stato in forms should be a select using 'statiMacroLookup' (picked up from columnConfig)
};


const FinalMacroFunzioniPage = () => {
    const {
        selectedModuleIds,
        cachedTabelleOptions,
        setCachedTabelleOptions,
        handleAdvancedFilterChange
    } = useMacroFunzioniState();

    // AGGIUNTO: Stato per forzare il refresh del TableManager
    const [refreshKey, setRefreshKey] = useState(0);

    const staticLookupConfig = useMemo(() => ({
        MAC_Id_Moduli_lookup: {
            tableName: 'SKE_Moduli',
            valueField: 'MOD_Id_Moduli',
            labelField: 'MOD_CodiceModulo', 
            additionalFields: ['MOD_DescrizioneModulo'],
        },
        MAC_Tabelle_lookup: {
            tableName: 'SKE_Tabelle',
            valueField: 'TAB_Id_Tabelle', 
            labelField: 'TAB_Descrizione', 
            additionalFields: ['TAB_Id_Moduli','TAB_Prefisso'],
            // RIMOSSO: filtri dinamici che causavano richieste continue
        },
        statiMacroLookup: { 
            tableName: 'DOM_StatiAttivita',
            valueField: 'STM_Codice', 
            labelField: 'STM_Descrizione', 
        },
    }), []); // Dipendenze vuote = configurazione statica

    // Hook per caricare le opzioni di lookup (solo una volta)
    const { lookupOptions: rawLookupOptions, loading: isLoadingLookups } = useLookupOptions(staticLookupConfig);

    // Memorizza le opzioni delle tabelle quando vengono caricate per la prima volta
    useEffect(() => {
        if (rawLookupOptions.MAC_Tabelle_lookup && cachedTabelleOptions.length === 0) {
            setCachedTabelleOptions(rawLookupOptions.MAC_Tabelle_lookup);
            // Opzioni tabelle cached (debug removed)
        }
    }, [rawLookupOptions.MAC_Tabelle_lookup, cachedTabelleOptions.length]);

    // Filtra dinamicamente le opzioni delle tabelle in base ai moduli selezionati
    const filteredLookupOptions = useMemo(() => {

        if (!rawLookupOptions || !cachedTabelleOptions.length) {
            return rawLookupOptions;
        }

        const filtered = { ...rawLookupOptions };

        if (selectedModuleIds.length > 0) {
            // Filtra le tabelle per mostrare solo quelle dei moduli selezionati
            const filteredTabelle = cachedTabelleOptions.filter(table => {

                const tableModuleId = table.original?.TAB_Id_Moduli;
                const isIncluded = selectedModuleIds.includes(tableModuleId);
                return isIncluded;
            });
            
            filtered.MAC_Tabelle_lookup = filteredTabelle;
            
            logger.debug('Filtro frontend applicato alle tabelle:', {
                moduliSelezionati: selectedModuleIds,
                tabelleOriginali: cachedTabelleOptions.length,
                tabelleFiltrate: filteredTabelle.length
            });
        } else {
            // Nessun modulo selezionato = mostra tutte le tabelle (già filtrate da filterTabelleForMacroFunzioniPanel)
            filtered.MAC_Tabelle_lookup = cachedTabelleOptions;

        }

        return filtered;
    }, [rawLookupOptions, cachedTabelleOptions, selectedModuleIds]);

    // --- MODIFICA CHIAVE ---
    // Definiamo i filtri iniziali come una costante memorizzata.
    // Questi sono i valori che vogliamo siano attivi al caricamento della pagina.
    const initialFilters = useMemo(() => ({
        MAC_Stato: ['I', 'C', 'F', 'P', 'M']
    }), []);

    // Modifichiamo la configurazione dei filtri per passare i valori iniziali al componente
    // in modo che i checkbox appaiano correttamente selezionati.
    const finalFilterConfig = useMemo(() => {
        return filterConfig.map(fc => {
            if (fc.field === 'advancedFilters') {
                return {
                    ...fc,
                    componentProps: {
                        initialStatoFilters: initialFilters.MAC_Stato, // Usa i filtri iniziali
                        // CORREZIONE: Passiamo la funzione con il nome corretto
                        onAdvancedFilterChange: handleAdvancedFilterChange,
                    }
                };
            }
            return fc;
        });
    }, [handleAdvancedFilterChange, initialFilters]); // Aggiunta dipendenza

    const defaultValuesForCreate = useMemo(() => {
        const defaults = {
            // Valori di default di base
            MAC_Stato: 'I',
            MAC_Priorita: 0,
            MAC_StatiRecord: 'A',
            
            // Valori di default per i campi MAC_Path e MAC_Organizzazione
            MAC_Path: 1,
            MAC_Organizzazione: 0, // MANTIENI come number 0
            MAC_Area_Params: 'ENV',
            MAC_Note: '',
            // Flag per funzioni CRUD predefinite attivati di default
            MAC_Add: true,
            MAC_Get: true,
            MAC_Src: true,
            MAC_Upd: true,
            MAC_Del: true,
            
            // Funzioni aggiuntive attivate di default
            MAC_Srl: true,
            MAC_Set: true,
            MAC_Rps: true,
            MAC_Prn: true,
            MAC_Syn: true,
            
            // Flag opzioni tabella attivati di default
            MAC_CreaItem: true,
            MAC_CreaItemFiltro: true,
            MAC_ControlloValidita: false, // DEV'ESSERE FALSE
            MAC_DatiInserimento: true,
            MAC_DatiModifica: true
        };
        
        // DEBUG LOG TEMPORANEO
        
        return defaults;
    }, []);

    // AGGIUNTO: Wrapper per passare la refreshKey e il callback alla DetailView
    const DetailViewWithRefresh = useCallback((props) => {

        return (
            <MacroFunzioniDetailView 
                {...props} 
                dataRefreshKey={refreshKey} 
                onDataRefreshRequest={() => {

                    setRefreshKey(prevKey => prevKey + 1);
                }} 
            />
        );
    }, [refreshKey]); // La dipendenza da refreshKey è cruciale

    return (
        <>
            {/* 🎉 Birthday Easter Egg per cristiana */}
            <BirthdayOverlay 
            
            
            
            />
            
            <TableManager
                key={`macro-funzioni-table-${refreshKey}`}
                tableName="SKE_MacroFunzioni"
                primaryKey="MAC_Id_MacroFunzioni"
                pageTitle="Gestione macrofunzioni"
                initialFilters={initialFilters}
                pinnedTopFilters={PINNED_MACRO_FILTERS}
                columnConfig={columnConfig}
                filterConfig={finalFilterConfig}
                lookupConfig={staticLookupConfig}
                lookupOptions={filteredLookupOptions}
                inputTypes={inputTypes}
                enableCreate={true}
                enableEdit={true}
                enableDelete={true}
                initialSortBy={[
                    { id: 'MAC_Priorita', desc: false },
                    { id: 'MAC_Nome', desc: false }
                ]}
                detailViewComponent={DetailViewWithRefresh}
                defaultValuesForCreate={defaultValuesForCreate}
                statusField="MAC_Stato" // Add this line to restore MacroFunzioni row coloring
            >
                <CustomActionButtons />
            </TableManager>
        </>
    );
};

export default FinalMacroFunzioniPage;
